/**
 * PodcastListView - Podcast table rendering and display
 *
 * Single Responsibility: Renders the podcast list table with individual podcast cards.
 * This component handles the visual presentation of podcast items in a scrollable list.
 *
 * Responsibilities:
 * - Display podcast cards with cover art, title, author, and metadata
 * - Show loading and empty states
 * - Handle podcast selection (click events)
 * - Render podcast type chips and status badges
 *
 * Does NOT handle:
 * - Data fetching or pagination logic (handled by parent)
 * - Filtering or search (handled by PodcastFilters)
 * - Pagination controls (handled by Pagination component)
 */
package com.castly.admin.ui.podcasts

import androidx.compose.runtime.Composable
import com.castly.admin.ui.*
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.PodcastDto
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Renders the podcast list container with cards
 */
@Composable
fun PodcastListView(
    podcasts: List<PodcastDto>,
    loading: Boolean,
    error: String?,
    selectedPodcastId: String?,
    searchQuery: String,
    onPodcastClick: (PodcastDto) -> Unit
) {
    val colors = AppTheme.colors
    // Error message
    error?.let { msg ->
        ErrorMessage(msg)
    }

    // Podcast list container
    Div({
        attr("data-testid", "podcast-list-container")
        style {
            backgroundColor(Color(colors.surface))
            borderRadius(BorderRadius.lg)
            border(1.px, LineStyle.Solid, Color(colors.border))
            property("overflow", "auto")
            flex("1")
            themeTransition()
        }
    }) {
        if (loading) {
            LoadingIndicator()
        } else if (podcasts.isEmpty()) {
            EmptyState(if (searchQuery.isNotBlank()) "No podcasts found" else "No podcasts yet. Create your first one!")
        } else {
            podcasts.forEach { podcast ->
                PodcastCard(
                    podcast = podcast,
                    isSelected = podcast.id == selectedPodcastId,
                    onClick = { onPodcastClick(podcast) }
                )
            }
        }
    }
}

/**
 * Individual podcast card component
 * Displays podcast information with cover art, title, author, and metadata badges
 */
@Composable
fun PodcastCard(
    podcast: PodcastDto,
    isSelected: Boolean = false,
    onClick: () -> Unit
) {
    val colors = AppTheme.colors
    Div({
        attr("data-testid", "podcast-card")
        attr("data-podcast-id", podcast.id)
        onClick { onClick() }
        style {
            padding(Spacing.lg)
            property("border-bottom", "1px solid ${colors.border}")
            cursor("pointer")
            property("transition", "background-color 0.2s")
            if (isSelected) {
                backgroundColor(Color(if (colors == LightColors) "#f0f9ff" else "#0c1929"))
            }
            display(DisplayStyle.Flex)
            gap(Spacing.md)
        }
        onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
        onMouseOut {
            if (!isSelected) {
                it.target.asDynamic().style.backgroundColor = "transparent"
            }
        }
    }) {
        // Cover art thumbnail (with fallback to original RSS image)
        val displayImageUrl = podcast.coverArtUrl ?: podcast.originalCoverArtUrl

        displayImageUrl?.let { imageUrl ->
            Img(imageUrl) {
                style {
                    width(60.px)
                    height(60.px)
                    borderRadius(BorderRadius.md)
                    property("object-fit", "cover")
                    flexShrink(0)
                }
            }
        } ?: run {
            Div({
                style {
                    width(60.px)
                    height(60.px)
                    backgroundColor(Color(colors.border))
                    borderRadius(BorderRadius.md)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(10.px)
                    color(Color(colors.textMuted))
                    flexShrink(0)
                }
            }) { Text("No Art") }
        }

        // Content container
        Div({
            style {
                flex("1")
            }
        }) {
            // Title
            Div({
                style {
                    fontWeight("600")
                    fontSize(Typography.lg)
                    color(Color(colors.text))
                    marginBottom(Spacing.xs)
                }
            }) { Text(podcast.title) }

            // Author
            if (podcast.author != null) {
                Div({
                    style {
                        fontSize(Typography.base)
                        color(Color(colors.textMuted))
                        marginBottom(Spacing.xs)
                    }
                }) { Text("by ${podcast.author}") }
            }

            // Badges (type, language, linked, featured)
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(Spacing.sm)
                    fontSize(Typography.sm)
                    marginBottom(Spacing.xs)
                }
            }) {
                // Podcast type chip
                PodcastTypeChip(podcast.type)

                StatusBadge(
                    text = podcast.language.uppercase(),
                    backgroundColor = colors.surfaceHover,
                    textColor = colors.primary
                )

                if (podcast.isLinked) {
                    StatusBadge(
                        text = "RSS Linked",
                        backgroundColor = if (colors == LightColors) "#dbeafe" else "#0c1929",
                        textColor = if (colors == LightColors) "#1e40af" else "#93c5fd"
                    )
                }
            }

            // Tag chips
            if (podcast.tags.isNotEmpty()) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(6.px)
                        fontSize(Typography.sm)
                        marginBottom(Spacing.xs)
                        property("flex-wrap", "wrap")
                    }
                }) {
                    podcast.tags.forEach { tag ->
                        StatusBadge(
                            text = tag.name,
                            backgroundColor = colors.border,
                            textColor = colors.text
                        )
                    }
                }
            }

            // Episode count
            if (podcast.episodeCount != null) {
                Div({
                    style {
                        fontSize(Typography.sm)
                        color(Color(colors.textMuted))
                    }
                }) { Text("${podcast.episodeCount} episodes") }
            }
        }
    }
}

/**
 * Podcast type chip with color-coded badge
 */
@Composable
fun PodcastTypeChip(type: String) {
    val podcastType = PodcastType.fromString(type)

    // Color mapping for each type
    val backgroundColor = when (podcastType) {
        PodcastType.PODCAST -> Color("#2196F3")           // Blue
        PodcastType.RADIO_SHOW -> Color("#9C27B0")        // Purple
        PodcastType.QURAAN -> Color("#4CAF50")            // Green
        PodcastType.TV_SERIES -> Color("#F44336")         // Red
        PodcastType.NEWS -> Color("#00BCD4")              // Cyan
        PodcastType.DOCUMENTARY -> Color("#009688")       // Teal
        PodcastType.COURSE -> Color("#FFC107")            // Amber
        PodcastType.BOOKS -> Color("#795548")              // Brown
    }

    Span({
        // Prevent mouse events from bubbling to parent podcast card
        onMouseOver { it.stopPropagation() }
        onMouseOut { it.stopPropagation() }
        style {
            display(DisplayStyle.InlineBlock)
            padding(Spacing.xs, 10.px)
            backgroundColor(backgroundColor)
            color(Color("#ffffff"))
            borderRadius(12.px)
            fontSize(Typography.xs)
            fontWeight("600")
            property("text-transform", "uppercase")
            property("letter-spacing", "0.5px")
        }
    }) {
        Text(podcastType.displayName)
    }
}
